import { create } from 'zustand'
import {
  persist,
  type PersistStorage,
  type StorageValue,
} from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'

export type SolicitationDocumentsFilterKey =
  | 'all'
  | 'pdf'
  | 'word'
  | 'excel'
  | 'archive'
  | 'image'

export interface SolicitationDocumentsPrefs {
  query: string
  activeFilter: SolicitationDocumentsFilterKey
  scrollTop: number
  selectedFileId: string | null
}

interface SolicitationDocumentsPrefsState {
  documentsByOpportunityId: Record<string, SolicitationDocumentsPrefs>
  setDocumentsPrefs: (
    opportunityId: string,
    prefs: Partial<SolicitationDocumentsPrefs>,
  ) => void
  clearDocumentsPrefs: (opportunityId: string) => void
}

type PersistedSolicitationDocumentsPrefsState = Pick<
  SolicitationDocumentsPrefsState,
  'documentsByOpportunityId'
>

const STORAGE_KEY = 'sweetspot-solicitation-documents-prefs'
const STORAGE_VERSION = 2

const FILTER_KEYS: readonly SolicitationDocumentsFilterKey[] = [
  'all',
  'pdf',
  'word',
  'excel',
  'archive',
  'image',
]

export const DEFAULT_SOLICITATION_DOCUMENTS_PREFS: SolicitationDocumentsPrefs =
  {
    query: '',
    activeFilter: 'all',
    scrollTop: 0,
    selectedFileId: null,
  }

function isFilterKey(value: unknown): value is SolicitationDocumentsFilterKey {
  return (
    typeof value === 'string' &&
    FILTER_KEYS.includes(value as SolicitationDocumentsFilterKey)
  )
}

function normalizeScrollTop(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0
  }

  return Math.max(0, Math.round(value))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function normalizePrefs(value: unknown): SolicitationDocumentsPrefs {
  if (!isRecord(value)) {
    return DEFAULT_SOLICITATION_DOCUMENTS_PREFS
  }

  const prefs = value as Partial<SolicitationDocumentsPrefs>

  return {
    query: typeof prefs.query === 'string' ? prefs.query : '',
    activeFilter: isFilterKey(prefs.activeFilter) ? prefs.activeFilter : 'all',
    scrollTop: normalizeScrollTop(prefs.scrollTop),
    selectedFileId:
      typeof prefs.selectedFileId === 'string' &&
      prefs.selectedFileId.length > 0
        ? prefs.selectedFileId
        : null,
  }
}

function getMigratedState(
  persisted: unknown,
): PersistedSolicitationDocumentsPrefsState {
  if (!isRecord(persisted)) {
    return { documentsByOpportunityId: {} }
  }

  const state = persisted as Partial<SolicitationDocumentsPrefsState>
  const persistedDocuments = state.documentsByOpportunityId

  if (!isRecord(persistedDocuments)) {
    return { documentsByOpportunityId: {} }
  }

  return {
    documentsByOpportunityId: Object.fromEntries(
      Object.entries(persistedDocuments)
        .filter(([opportunityId]) => opportunityId.length > 0)
        .map(([opportunityId, prefs]) => [
          opportunityId,
          normalizePrefs(prefs),
        ]),
    ),
  }
}

function isPersistedStorageValue(
  value: unknown,
): value is Partial<StorageValue<unknown>> {
  return isRecord(value) && 'state' in value
}

function getSafeSolicitationDocumentsPrefsStorage():
  | PersistStorage<PersistedSolicitationDocumentsPrefsState>
  | undefined {
  if (typeof window === 'undefined') {
    return undefined
  }

  return {
    getItem: (name) => {
      let rawValue: string | null

      try {
        rawValue = window.localStorage.getItem(name)
      } catch {
        return null
      }

      if (!rawValue) return null

      let parsedValue: unknown
      try {
        parsedValue = JSON.parse(rawValue)
      } catch {
        try {
          window.localStorage.removeItem(name)
        } catch {
          // Ignore storage access failures; defaults are safer than crashing.
        }
        return null
      }

      if (!isPersistedStorageValue(parsedValue)) {
        return {
          state: { documentsByOpportunityId: {} },
          version: STORAGE_VERSION,
        }
      }

      return {
        state: getMigratedState(parsedValue.state),
        version:
          typeof parsedValue.version === 'number'
            ? parsedValue.version
            : STORAGE_VERSION,
      }
    },
    setItem: (name, value) => {
      try {
        window.localStorage.setItem(name, JSON.stringify(value))
      } catch {
        // Persisted UI preferences are optional; never block the app on them.
      }
    },
    removeItem: (name) => {
      try {
        window.localStorage.removeItem(name)
      } catch {
        // Persisted UI preferences are optional; never block the app on them.
      }
    },
  }
}

export const useSolicitationDocumentsPrefsStore =
  create<SolicitationDocumentsPrefsState>()(
    persist(
      (set) => ({
        documentsByOpportunityId: {},
        setDocumentsPrefs: (opportunityId, prefs) =>
          set((state) => ({
            documentsByOpportunityId: {
              ...state.documentsByOpportunityId,
              [opportunityId]: normalizePrefs({
                ...state.documentsByOpportunityId[opportunityId],
                ...prefs,
              }),
            },
          })),
        clearDocumentsPrefs: (opportunityId) =>
          set((state) => ({
            documentsByOpportunityId: Object.fromEntries(
              Object.entries(state.documentsByOpportunityId).filter(
                ([existingOpportunityId]) =>
                  existingOpportunityId !== opportunityId,
              ),
            ),
          })),
      }),
      {
        name: STORAGE_KEY,
        version: STORAGE_VERSION,
        storage: getSafeSolicitationDocumentsPrefsStorage(),
        partialize: (state) => ({
          documentsByOpportunityId: state.documentsByOpportunityId,
        }),
        migrate: (persisted) => getMigratedState(persisted),
      },
    ),
  )

export const useSolicitationDocumentsPrefs = (opportunityId?: string | null) =>
  useSolicitationDocumentsPrefsStore(
    useShallow((state) =>
      opportunityId
        ? (state.documentsByOpportunityId[opportunityId] ??
          DEFAULT_SOLICITATION_DOCUMENTS_PREFS)
        : DEFAULT_SOLICITATION_DOCUMENTS_PREFS,
    ),
  )

export const useSetSolicitationDocumentsPrefs = () =>
  useSolicitationDocumentsPrefsStore((state) => state.setDocumentsPrefs)
